import type { RExecHttpRequest } from 'v2/pages/flow/sidebar/types'
import { FlowNodeTypes } from '../types'
import { SCREENING_NODE_VARS } from './contact-screening.config'

// User can add any url in https component and it's response could be on any type. We cannot predefine it.
const extractPaths = (data: unknown, prefix = ''): string[] => {
  const parsedData = typeof data === 'string' ? JSON.parse(data) : data
  if (!parsedData) return []
  const paths: string[] = []

  function traverse(obj: unknown, currentPath: string): void {
    if (Array.isArray(obj)) {
      // Include the array itself as a referenceable path
      if (currentPath) paths.push(currentPath)

      obj.forEach((item, index) => {
        traverse(item, `${currentPath}.${index}`)
      })
    } else if (typeof obj === 'object' && obj !== null) {
      // Include intermediate objects as referenceable paths
      if (currentPath) paths.push(currentPath)

      Object.entries(obj).forEach(([key, value]) => {
        traverse(value, `${currentPath}.${key}`)
      })
    } else {
      // Include primitive values
      paths.push(currentPath)
    }
  }

  traverse(parsedData, prefix)
  return paths
}

export type NodeVarType =
  | null
  | (Record<string, string[]> & {
      default: string[]
    })

interface GetNodeVarsProps {
  meta?: {
    httpResp?: RExecHttpRequest['response_format'] | null
    aiAgent?: string[] | null
    channel?: string
    aiAgentV2?: string[] | null
    agentNode?: string[] | null
  }
}
const getAiAgentNodeVars = (channel: string) => {
  if (channel === 'message')
    return {
      preset_message: [
        'outgoing_message.uuid',
        'outgoing_message.to',
        'outgoing_message.from',
        'outgoing_message.text',
        'outgoing_message.from_iso2',
        'outgoing_message.from_country',
        'outgoing_message.to_iso2',
        'outgoing_message.to_country',
        'outgoing_message.media_urls',
      ],
      preset_message_with_response: [
        'incoming_message.text',
        'incoming_message.uuid',
        'incoming_message.media_urls',
        'conversation_id',
      ],
    }
  if (channel === 'whatsapp') {
    return {
      preset_message: ['incoming_message.order', 'incoming_message.text'],
      preset_message_with_response: ['incoming_message.text'],
    }
  }
  if (channel === 'chat') {
    return {
      preset_message: ['outgoing_message.text', 'outgoing_message.media_urls', 'status'],
      preset_message_with_response: ['incoming_message.text'],
    }
  }
  return {
    preset_message: [],
    preset_message_with_response: [],
  }
}
// DEFAULT NODE VARS FOR EACH NODE TYPE
// Note: while adding a new node type, make sure to add the default node vars here
// Add default node vars for each node type
// For specific node types related to handles (output states), add the keys as the handle names
// The handle name and the node var key name should be same
export const getDefaultNodeVars = ({
  meta,
}: GetNodeVarsProps): Record<FlowNodeTypes, NodeVarType> => ({
  [FlowNodeTypes.menu]: null,
  [FlowNodeTypes.multi_party_call_v2]: {
    default: [
      'from',
      'to',
      'direction',
      'caller_name',
      'bill_rate',
      'call_status',
      'call_uuid',
      'is_customer',
      'role',
      'conference_name',
      'conference_uuid',
      'member_id',
      'country',
    ],
  },

  [FlowNodeTypes.conference_bridge]: {
    default: [
      'from',
      'to',
      'direction',
      'caller_name',
      'event',
      'bill_rate',
      'call_status',
      'conference_name',
      'call_uuid',
      'conference_uuid',
      'member_id',
      'country',
    ],
  },
  [FlowNodeTypes.stream]: null, // NODE_GEN: update as required

  [FlowNodeTypes.agent_preset_conversation]: {
    default: [...(meta?.aiAgent || [])],
    ...getAiAgentNodeVars(meta?.channel || 'message'),
  },
  [FlowNodeTypes.ai_skills]: {
    default: ['skill_result'],
  },
  [FlowNodeTypes.note]: null,
  [FlowNodeTypes.end_conversation]: null, // NODE_GEN: update as required
  [FlowNodeTypes.ai_agent_v2]: {
    default: [...(meta?.aiAgentV2 || [])],
    ...getAiAgentNodeVars(meta?.channel || 'message'),
  },
  [FlowNodeTypes.ai_agent_v29]: {
    // The outputs chips (record-as names) are what collect_input will record.
    default: [...(meta?.aiAgentV2 || [])],
    ...getAiAgentNodeVars(meta?.channel || 'message'),
  },
  [FlowNodeTypes.agent_node]: {
    default: [...(meta?.agentNode || [])],
    ...getAiAgentNodeVars('call'),
  },
  [FlowNodeTypes.contact_screening]: {
    default: SCREENING_NODE_VARS,
  },
  [FlowNodeTypes.outbound_screening]: {
    default: SCREENING_NODE_VARS,
  },

  [FlowNodeTypes.ai_action]: {
    default: ['intent', 'response', 'success'],
  },

  [FlowNodeTypes.select_trigger]: null,
  [FlowNodeTypes.initiate_call]: {
    default: [
      'uuid',
      'to',
      'from',
      'direction',
      'from_iso2',
      'leg',
      'bill_rate',
      'from_country',
      'voicemail_detected',
      'call_status',
      'conversation_id',
    ],
  },
  [FlowNodeTypes.ai_assist]: null, // NODE_GEN: update as required

  [FlowNodeTypes.user_input]: null, // NODE_GEN: update as required

  [FlowNodeTypes.ai_agent_call]: {
    default: [...(meta?.aiAgent || [])],
  }, // NODE_GEN: update as required

  [FlowNodeTypes.ai_agent_message]: {
    default: [...(meta?.aiAgent || [])],
    preset_message: [
      'outgoing_message.uuid',
      'outgoing_message.to',
      'outgoing_message.from',
      'outgoing_message.text',
      'outgoing_message.from_iso2',
      'outgoing_message.from_country',
      'outgoing_message.to_iso2',
      'outgoing_message.to_country',
      'outgoing_message.media_urls',
    ],
    preset_message_with_response: [
      'incoming_message.text',
      'incoming_message.uuid',
      'incoming_message.media_urls',
      'conversation_id',
    ],
  },

  [FlowNodeTypes.ai_agent_chat]: {
    default: [...(meta?.aiAgent || [])],
    preset_message: ['outgoing_message.text', 'outgoing_message.media_urls', 'status'],
    preset_message_with_response: ['incoming_message.text'],
  }, // NODE_GEN: update as required

  [FlowNodeTypes.ai_agent_whatsapp]: {
    default: [...(meta?.aiAgent || [])],
    preset_message: [
      'outgoing_message.text',
      'outgoing_message.media_urls',
      'outgoing_message.to',
      'outgoing_message.from',
    ],
    preset_message_with_response: ['incoming_message.order', 'incoming_message.text'],
  }, // NODE_GEN: update as required

  [FlowNodeTypes.branch_v2]: null, // NODE_GEN: update as required

  [FlowNodeTypes.send_whatsapp]: null, // NODE_GEN: update as required

  [FlowNodeTypes.send_chat]: null, // NODE_GEN: update as required

  [FlowNodeTypes.send_message]: {
    default: [
      'outgoing_message.uuid',
      'outgoing_message.to',
      'outgoing_message.from',
      'outgoing_message.from_iso2',
      'outgoing_message.from_country',
      'outgoing_message.to_iso2',
      'outgoing_message.to_country',
      'outgoing_message.text',
      'outgoing_message.media_urls',
      'incoming_message.uuid',
      'incoming_message.text',
      'incoming_message.media_urls',
      'conversation_id',
      'status',
      'phlo_action',
    ],
  }, // NODE_GEN: update as required

  [FlowNodeTypes.multi_party_call_outbound]: null, // NODE_GEN: update as required

  [FlowNodeTypes.whatsapp_conversation]: null, // NODE_GEN: update as required

  [FlowNodeTypes.message_conversation]: null, // NODE_GEN: update as required

  [FlowNodeTypes.business_hour]: null, // NODE_GEN: update as required

  [FlowNodeTypes.app_action]: null, // NODE_GEN: update as required
  [FlowNodeTypes.http_request]: {
    default: [
      'content_type',
      'response',
      'status_code',
      ...extractPaths(meta?.httpResp, 'response'),
    ],
  }, // NODE_GEN: update as required

  [FlowNodeTypes.create_task]: null, // NODE_GEN: update as required

  [FlowNodeTypes.counter]: null, // NODE_GEN: update as required

  [FlowNodeTypes.queue_and_route]: {
    default: [
      'assigned_agent_email',
      'assigned_agent_name',
      'conversation_id',
      'conversation_created_datetime',
      'channels',
      'duration',
      'status',
      'disposition',
      'wrapup_note',
      'associated_contact_id',
      'associated_ticket_id',
    ],
  }, // NODE_GEN: update as required

  [FlowNodeTypes.customer_feedback]: {
    default: ['input'],
  },

  [FlowNodeTypes.get_input]: {
    default: ['input', 'input_type'],
  },
  [FlowNodeTypes.voicemail]: {
    default: [
      'call_uuid',
      'digits',
      'recording_url',
      'recording_uuid',
      'recording_duration',
      'recording_start_time',
      'recording_end_time',
    ],
  },
  [FlowNodeTypes.prompt]: null,
  [FlowNodeTypes.call_forward]: {
    default: ['uuid', 'to', 'from', 'direction', 'from_iso2', 'from_country', 'dial_status'],
  },
  [FlowNodeTypes.ivr_menu]: {
    default: ['digits'],
  },
  [FlowNodeTypes.start]: {
    default: ['request_type', 'flow_run_id'],
    call: [
      'uuid',
      'to',
      'from',
      'direction',
      'to_iso2',
      'from_iso2',
      'to_country',
      'from_country',
      'leg',
      'bill_rate',
      'header1',
      'header2',
      'header3',
      'header4',
      'header5',
      'header6',
      'conversation_id',
      'conversation_url',
    ],
    chat: [
      'chat_uuid',
      'from',
      'customer_user_status',
      'from_iso2',
      'from_country',
      'customer_first_name',
      'customer_last_name',
      'customer_phone',
      'customer_email',
      'customer_custom_data',
      'conversation_custom_data',
      'client_data',
      'external_identifier',
      'conversation_id',
      'conversation_url',
    ],
    destination: [
      'transfer_conversations.customer_address',
      'transfer_conversations.customer_country',
      'transfer_conversations.customer_country_iso2',
      'transfer_conversations.transfer_type',
      'transfer_conversations.transferred_by',
      'transfer_conversations.transferred_to',
      'transfer_conversations.conversation_id',
      'transfer_conversations.conversation_url',
      'tasks.task_to',
      'tasks.from_name',
      'tasks.description',
      'tasks.task_uuid',
      'tasks.task_type',
      'destination_type',
    ],
    email: [
      'email_uuid',
      'from',
      'reciever_email',
      'to',
      'cc',
      'bcc',
      'subject',
      'msg',
      'conversation_id',
      'conversation_url',
    ],
    http: ['params'],
    message: [
      'message_uuid',
      'direction',
      'status',
      'to',
      'from',
      'to_iso2',
      'from_iso2',
      'to_country',
      'from_country',
      'msg',
      'units',
      'total_rate',
      'total_amount',
      'type',
      'media_count',
      'media_urls',
      'media_url1',
      'media_url2',
      'media_url3',
      'media_url4',
      'media_url5',
      'media_url6',
      'media_url7',
      'media_url8',
      'media_url9',
      'media_url10',
      'conversation_id',
      'conversation_url',
    ],
    outbound_call: [
      'uuid',
      'to',
      'from',
      'direction',
      'to_iso2',
      'from_iso2',
      'to_country',
      'from_country',
      'leg',
      'bill_rate',
      'record',
      'header1',
      'header2',
      'header3',
      'header4',
      'header5',
      'conversation_id',
      'conversation_url',
    ],
    outbound_email: [
      'email_uuid',
      'from',
      'reciever_email',
      'to',
      'cc',
      'bcc',
      'subject',
      'msg',
      'conversation_id',
      'conversation_url',
    ],
    outbound_message: [
      'message_uuid',
      'from',
      'to',
      'msg',
      'media_url1',
      'media_url2',
      'media_url3',
      'media_url4',
      'media_url5',
      'media_url6',
      'media_url7',
      'media_url8',
      'media_url9',
      'media_url10',
      'conversation_id',
      'conversation_url',
    ],
    outbound_whatsapp: [
      'whatsapp_uuid',
      'from',
      'to',
      'template_attributes',
      'conversation_id',
      'conversation_url',
    ],
    whatsapp: ['whatsapp_uuid', 'from', 'to', 'conversation_id', 'conversation_url'],
    incoming_slack: [
      'customer_first_name',
      'customer_last_name',
      'customer_email',
      'customer_phone',
    ],
  },
  [FlowNodeTypes.hangup]: null,
})
